import { useCallback, useEffect, useState } from 'react';

import { searchAdmissions } from '../../services/api/admissionApi';

/**
 * useAdmissionServerSearch — org-wide, all-status desk search (any age) behind
 * the suggestion dropdown. `q` is the ALREADY-DEBOUNCED query (the search box
 * debounces ~280 ms before it reaches the URL filter state).
 *
 * RUNTIME SAFETY: the effect depends only on primitives (normalised key + retry
 * counter). A new key aborts the previous request (cleanup) and its late
 * response is ignored (`live` flag = stale guard). Same query within 60 s is
 * served from a module cache (0 calls). Under 2 letters/digits → 0 calls. A
 * failure is never cached and never auto-retried (429 included); `retry()` is
 * the only re-issue.
 */
export const SEARCH_MIN_CHARS = 2;
const CACHE_TTL_MS = 60 * 1000;
const RESULT_LIMIT = 20;

const EMPTY = [];
const CACHE = new Map(); // key -> { at, rows, truncated }

/** Test-only: reset the module cache between cases. */
export function __resetAdmissionServerSearch() { CACHE.clear(); }
/** Drop the per-query cache after a desk action (called with the index invalidate). */
export function invalidateAdmissionServerSearch() { CACHE.clear(); }
/** Logout / account switch: drop the per-query cache so the next login can't see these rows. */
export const clearAdmissionServerSearch = invalidateAdmissionServerSearch;

const alnumCount = (q) => ((q || '').match(/[\p{L}\p{N}]/gu) || []).length;
export const isSearchableQuery = (q) => alnumCount(q) >= SEARCH_MIN_CHARS;

export const SEARCH_STATUS = { IDLE: 'idle', LOADING: 'loading', OK: 'ok', ERROR: 'error' };
export const SEARCH_ERROR = { RATE_LIMITED: 'RATE_LIMITED', FAILED: 'SEARCH_FAILED' };

export function useAdmissionServerSearch(q) {
  const trimmed = (q || '').trim();
  const eligible = isSearchableQuery(trimmed);
  const key = trimmed.toLowerCase();
  const [result, setResult] = useState({ key: '', status: SEARCH_STATUS.IDLE });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!eligible) return undefined;
    const hit = CACHE.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) {
      setResult({ key, status: SEARCH_STATUS.OK, rows: hit.rows, truncated: hit.truncated });
      return undefined;
    }
    let live = true;
    const ctrl = new AbortController();
    setResult({ key, status: SEARCH_STATUS.LOADING });
    searchAdmissions({ q: trimmed, limit: RESULT_LIMIT }, { signal: ctrl.signal }).then((res) => {
      if (!live) return;
      if (res.success) {
        const { admissions, truncated } = res.data;
        CACHE.set(key, { at: Date.now(), rows: admissions, truncated });
        setResult({ key, status: SEARCH_STATUS.OK, rows: admissions, truncated });
      } else if (!res.aborted) {
        setResult({
          key,
          status: SEARCH_STATUS.ERROR,
          errorCode: res.status === 429 ? SEARCH_ERROR.RATE_LIMITED : SEARCH_ERROR.FAILED,
        });
      }
    });
    return () => { live = false; ctrl.abort(); };
    // `trimmed` is derived from `key`'s source; key + attempt are the triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  if (!eligible) return { status: SEARCH_STATUS.IDLE, rows: EMPTY, truncated: false, errorCode: null, retry };
  if (result.key !== key) return { status: SEARCH_STATUS.LOADING, rows: EMPTY, truncated: false, errorCode: null, retry };
  return {
    status: result.status,
    rows: result.rows || EMPTY,
    truncated: result.truncated === true,
    errorCode: result.errorCode || null,
    retry,
  };
}
